import clsx from 'clsx';
import React from 'react';

import { Image } from '@unpic/react';

import { IconShortLine, SkyHomePhoneNumber, SkyHomePhoneProvider } from '@/assets/icons';
import { Size, useScreenSize } from '@/hooks/useScreenSize';
import { MODAL_TYPE } from '@/lib/constants';
import { MODAL_INFO_TYPE } from '@/lib/notification-message';

import { commonForLRSpacingClassMap } from '../../variants/confirmation-dialog/style';
import {
  dialogTitleContainerClassMap,
  dialogTitleImgContainerClassMap,
  textTitleClassMap,
  titleImgClassMap,
} from './style';

const thumbnailIconMap: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  SkyHomePhoneNumber,
  SkyHomePhoneProvider,
};

interface HeaderSectionProps {
  thumbnail?: { url: string };
  title?: string;
  handleReset: () => void;
  isShortLine?: boolean;
  size?: Size;
  type?: string;
}

const HeaderSection: React.FC<HeaderSectionProps> = ({
  thumbnail,
  title,
  handleReset,
  isShortLine,
  size,
  type,
}) => {
  const { screenSize, getSize: actualSize } = useScreenSize();
  const getSize = size || actualSize;
  const ThumbnailIcon = thumbnail?.url ? thumbnailIconMap[thumbnail.url] : undefined;

  const isContentOnlyModal = type === MODAL_TYPE.MORE_INFO || type === MODAL_TYPE.CHANNEL_INFO;

  const shouldRenderThumbnailIcon =
    type === MODAL_TYPE.SKY_HOME_PHONE_NUMBER || type === MODAL_TYPE.SKY_HOME_PHONE_PROVIDER;

  const responsiveContainerClass = clsx(
    { 'py-8': screenSize.width <= 1023 },
    { 'pb-8': screenSize.width >= 1023 },
    { [dialogTitleImgContainerClassMap[getSize]]: !isContentOnlyModal }
  );

  const contentWrapperClass = clsx(
    { 'inline-flex': !isContentOnlyModal },
    { [commonForLRSpacingClassMap[getSize]]: !isContentOnlyModal && screenSize.width >= 1023 }
  );

  return (
    <div>
      {screenSize.width <= 1023 && isShortLine ? (
        <div
          onClick={handleReset}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleReset();
            }
          }}
          role="button"
          tabIndex={0}
          className="flex cursor-pointer items-center justify-center"
          data-testid="reset"
        >
          <IconShortLine width={27} height={27} />
        </div>
      ) : null}
      {thumbnail?.url ? (
        <div className={responsiveContainerClass}>
          <div className={contentWrapperClass}>
            {shouldRenderThumbnailIcon && ThumbnailIcon ? (
              <ThumbnailIcon
                className={clsx(titleImgClassMap[getSize], 'w-full')}
                aria-hidden="true"
              />
            ) : (
              <Image
                background="auto"
                layout="fullWidth"
                className={clsx(titleImgClassMap[getSize])}
                alt="logo"
                src={thumbnail.url}
                loading="lazy"
              />
            )}
          </div>
        </div>
      ) : type === MODAL_INFO_TYPE.ESTIMATE_BILL_INFO ? (
        <div className={responsiveContainerClass} aria-label="Dialog Title Container">
          <div className={contentWrapperClass} aria-label="Dialog Title">
            <p className={clsx(textTitleClassMap[getSize])}>{title}</p>
          </div>
        </div>
      ) : (
        <div
          className={clsx(dialogTitleContainerClassMap[getSize], 'flex justify-center text-center')}
          aria-label="Dialog Title Container"
        >
          <p className={clsx(textTitleClassMap[getSize])}>{title}</p>
        </div>
      )}
    </div>
  );
};

export default HeaderSection;
